import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import CRMLayout from '../../components/CRM/CRMLayout';
import WorkspaceHeader from '../../components/CRM/workspace/WorkspaceHeader';
import OverviewTab from '../../components/CRM/workspace/OverviewTab';
import ApplicationTab from '../../components/CRM/workspace/ApplicationTab';
import BusinessTab from '../../components/CRM/workspace/BusinessTab';
import IncomeTab from '../../components/CRM/workspace/IncomeTab';
import LiabilitiesTab from '../../components/CRM/workspace/LiabilitiesTab';
import ApplicantsTab from '../../components/CRM/workspace/ApplicantsTab';
import AssetsTab from '../../components/CRM/workspace/AssetsTab';
import NetWorthTab from '../../components/CRM/workspace/NetWorthTab';
import DocumentsTab from '../../components/CRM/workspace/DocumentsTab';
import SubmissionsTab from '../../components/CRM/workspace/SubmissionsTab';
import TasksTab from '../../components/CRM/workspace/TasksTab';
import NotesTab from '../../components/CRM/workspace/NotesTab';
import SettlementTab from '../../components/CRM/workspace/SettlementTab';
import AiTab from '../../components/CRM/workspace/AiTab';
import ComplianceTab from '../../components/CRM/workspace/ComplianceTab';
import { financeRules, isConsumerFinanceType } from '../../utils/financeType';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BASE_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'application', label: 'Application' },
  { id: 'applicants', label: 'Applicants' },
  { id: 'business', label: 'Business' },
  { id: 'income', label: 'Income' },
  { id: 'liabilities', label: 'Liabilities' },
  { id: 'nccp', label: 'NCCP' },
  { id: 'assets', label: 'Asset (Deal Security)' },
  { id: 'net-worth', label: 'Net Worth' },
  { id: 'documents', label: 'Documents' },
  { id: 'submissions', label: 'Submissions' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'notes', label: 'Notes / Emails' },
  { id: 'settlement', label: 'Settlement' },
  { id: 'ai', label: 'AI' },
];

export default function DealWorkspace() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const [deal, setDeal] = useState(null);
  const [completeness, setCompleteness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchDeal = useCallback(async () => {
    try {
      const [dealRes, compRes] = await Promise.all([
        axios.get(`${API}/crm/deals/${dealId}`),
        axios.get(`${API}/crm/deals/${dealId}/completeness`),
      ]);
      setDeal(dealRes.data);
      setCompleteness(compRes.data);
    } catch (error) {
      toast.error('Failed to load deal');
      navigate('/crm/pipeline');
    } finally {
      setLoading(false);
    }
  }, [dealId, navigate]);

  useEffect(() => { fetchDeal(); }, [fetchDeal]);

  if (loading) return (
    <CRMLayout>
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin h-8 w-8 border-2 border-purple-400 border-t-transparent rounded-full" />
      </div>
    </CRMLayout>
  );

  if (!deal) return null;

  const rules = financeRules(deal.finance_type);
  const isConsumer = isConsumerFinanceType(deal.finance_type);
  const TABS = BASE_TABS.filter((tab) => {
    if (tab.id === 'assets') return rules.requiresAsset;
    if (tab.id === 'nccp') return isConsumer;
    return true;
  });
  const effectiveTab = !rules.requiresAsset && activeTab === 'assets'
    ? 'overview'
    : (!isConsumer && activeTab === 'nccp' ? 'overview' : activeTab);

  const tabProps = { deal, dealId, completeness, onRefresh: fetchDeal };

  return (
    <CRMLayout>
      <div className="flex flex-col h-screen" data-testid="deal-workspace">
        <WorkspaceHeader deal={deal} completeness={completeness} onRefresh={fetchDeal} />
        {/* Tab Bar */}
        <div className="border-b border-white/10 bg-navy-950/50 px-6 overflow-x-auto">
          <div className="flex gap-0" data-testid="workspace-tabs">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  effectiveTab === tab.id
                    ? 'border-purple-500 text-purple-400'
                    : 'border-transparent text-gray-400 hover:text-white hover:border-white/20'
                }`}>
                {tab.label}
                {tab.id === 'documents' && completeness?.counts?.docs_total > 0 && (
                  <span className="ml-1.5 text-xs text-gray-500">
                    {completeness.counts.docs_received}/{completeness.counts.docs_total}
                  </span>
                )}
                {tab.id === 'tasks' && completeness?.counts?.tasks_overdue > 0 && (
                  <span className="ml-1.5 text-xs bg-red-500/20 text-red-400 px-1.5 rounded-full">
                    {completeness.counts.tasks_overdue}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {effectiveTab === 'overview' && <OverviewTab {...tabProps} onTabChange={setActiveTab} />}
          {effectiveTab === 'application' && <ApplicationTab {...tabProps} />}
          {effectiveTab === 'business' && <BusinessTab {...tabProps} />}
          {effectiveTab === 'income' && <IncomeTab {...tabProps} />}
          {effectiveTab === 'liabilities' && <LiabilitiesTab {...tabProps} />}
          {effectiveTab === 'nccp' && <ComplianceTab {...tabProps} />}
          {effectiveTab === 'applicants' && <ApplicantsTab {...tabProps} />}
          {effectiveTab === 'assets' && <AssetsTab {...tabProps} />}
          {effectiveTab === 'net-worth' && <NetWorthTab {...tabProps} />}
          {effectiveTab === 'documents' && <DocumentsTab {...tabProps} />}
          {effectiveTab === 'submissions' && <SubmissionsTab {...tabProps} />}
          {effectiveTab === 'tasks' && <TasksTab {...tabProps} />}
          {effectiveTab === 'notes' && <NotesTab {...tabProps} />}
          {effectiveTab === 'settlement' && <SettlementTab {...tabProps} />}
          {effectiveTab === 'ai' && <AiTab {...tabProps} />}
        </div>
      </div>
    </CRMLayout>
  );
}
